import { translate } from '../i18n/locale.mjs'
const TOKEN_KEY = 'zhin_api_token'
const API_BASE_KEY = 'zhin_api_base'
const SAVED_LOGINS_KEY = 'zhin_saved_logins'
const MAX_SAVED_LOGINS = 20

type ZhinWindow = Window & { __ZHIN_API_TOKEN?: string }

export interface SavedLogin {
  apiBase: string
  token: string
  savedAt: number
  lastUsedAt: number
}

function trimTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value
}

/** Demo / 构建预置 Token：优先于 localStorage（对齐 @zhin.js/client remoteApi） */
function getRuntimeToken(): string | null {
  if (typeof window === 'undefined') return null
  const token = (window as ZhinWindow).__ZHIN_API_TOKEN
  return token?.trim() || null
}

export function getApiBase(): string {
  const stored = localStorage.getItem(API_BASE_KEY)?.trim()
  if (stored) return trimTrailingSlash(stored)
  if (typeof window !== 'undefined') return window.location.origin
  return ''
}

export function setApiBase(base: string): void {
  localStorage.setItem(API_BASE_KEY, trimTrailingSlash(base))
}

export function getToken(): string | null {
  return getRuntimeToken() ?? localStorage.getItem(TOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string, persistent = true): void {
  localStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(TOKEN_KEY)
  const storage = persistent ? localStorage : sessionStorage
  storage.setItem(TOKEN_KEY, token)
}

/** 写入运行时 Token（不落盘；Demo 用） */
export function setRuntimeToken(token: string): void {
  if (typeof window === 'undefined') return
  ;(window as ZhinWindow).__ZHIN_API_TOKEN = token.trim()
}

export function clearRuntimeToken(): void {
  if (typeof window === 'undefined') return
  delete (window as ZhinWindow).__ZHIN_API_TOKEN
}

export function clearToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
  localStorage.removeItem(TOKEN_KEY)
}

export function clearApiBase(): void {
  localStorage.removeItem(API_BASE_KEY)
}

export function hasToken(): boolean {
  return !!getToken()
}

export function normalizeApiBase(base: string): string {
  return trimTrailingSlash(base.trim())
}

/** 仅读 localStorage 中的 Host，不含 Pages 站点 origin 回退 */
export function getStoredApiBase(): string | null {
  const stored = localStorage.getItem(API_BASE_KEY)?.trim()
  return stored ? normalizeApiBase(stored) : null
}

export function clearSession(): void {
  clearToken()
  clearApiBase()
}

export function listSavedLogins(): SavedLogin[] {
  try {
    const raw = localStorage.getItem(SAVED_LOGINS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as SavedLogin[]
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((x) => x?.apiBase && x?.token)
      .sort((a, b) => (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0))
  } catch {
    return []
  }
}

export function getSavedLogin(apiBase: string): SavedLogin | null {
  const base = normalizeApiBase(apiBase)
  return listSavedLogins().find((x) => normalizeApiBase(x.apiBase) === base) ?? null
}

export function upsertSavedLogin(apiBase: string, token: string): void {
  const base = normalizeApiBase(apiBase)
  const trimmed = token.trim().replace(/^Bearer\s+/i, '')
  if (!base || !trimmed) return

  const now = Date.now()
  const rest = listSavedLogins().filter((x) => normalizeApiBase(x.apiBase) !== base)
  const existing = listSavedLogins().find((x) => normalizeApiBase(x.apiBase) === base)
  rest.unshift({
    apiBase: base,
    token: trimmed,
    savedAt: existing?.savedAt ?? now,
    lastUsedAt: now,
  })
  localStorage.setItem(SAVED_LOGINS_KEY, JSON.stringify(rest.slice(0, MAX_SAVED_LOGINS)))
}

export function removeSavedLogin(apiBase: string): void {
  const base = normalizeApiBase(apiBase)
  const rest = listSavedLogins().filter((x) => normalizeApiBase(x.apiBase) !== base)
  localStorage.setItem(SAVED_LOGINS_KEY, JSON.stringify(rest))
}

export function touchSavedLogin(apiBase: string): void {
  const base = normalizeApiBase(apiBase)
  const list = listSavedLogins()
  const idx = list.findIndex((x) => normalizeApiBase(x.apiBase) === base)
  if (idx < 0) return
  list[idx] = { ...list[idx], lastUsedAt: Date.now() }
  localStorage.setItem(SAVED_LOGINS_KEY, JSON.stringify(list))
}

/**
 * token 与 apiBase 成对缓存：URL 预填的 Host 与缓存不一致时清除登录态。
 */
export function reconcileAuthWithApiBase(incomingApiBase: string | null): {
  authed: boolean
  loginApiBase: string | null
} {
  const token = getToken()
  const stored = getStoredApiBase()
  const incoming = incomingApiBase ? normalizeApiBase(incomingApiBase) : null

  if (incoming) {
    if (token && stored !== incoming) {
      clearSession()
      return { authed: false, loginApiBase: incoming }
    }
    if (token && stored === incoming) {
      return { authed: true, loginApiBase: incoming }
    }
    return { authed: false, loginApiBase: incoming }
  }

  if (token && stored) {
    return { authed: true, loginApiBase: null }
  }

  if (token) {
    clearSession()
  }
  return { authed: false, loginApiBase: stored }
}

export function notifyAuthRequired(): void {
  // Demo 预置的 runtime token 过期后同样要失效，否则 getToken() 永远优先命中它，造成 401 死循环；
  // 重新登录（verifyAndStoreCredentials 成功）会再次写入新的 token。
  clearRuntimeToken()
  clearToken()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('zhin:auth-required'))
  }
}

export function clearSessionAndNotify(): void {
  clearSession()
  notifyAuthRequired()
}

export const QUERY_API_BASE_URL = "apiBaseUrl";

/** 仅允许 URL 预填 Host 地址；token 不得出现在 URL 中 */
export function readApiBaseUrlFromQuery(
  search = typeof window !== "undefined" ? window.location.search : "",
): string | null {
  const apiBaseUrl = new URLSearchParams(search).get(QUERY_API_BASE_URL)?.trim();
  return apiBaseUrl || null;
}

export function stripApiBaseUrlFromQuery(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(QUERY_API_BASE_URL)) return;
  url.searchParams.delete(QUERY_API_BASE_URL);
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, "", next);
}

export type VerifyCredentialsResult =
  | { ok: true }
  | { ok: false; message: string; status?: number }

export type ProbeHealthResult =
  | { ok: true }
  | { ok: false; message: string }

/** 登录前探测 Host 连通性（无需 Token） */
export async function probeHealth(apiBase: string, locale: 'zh-CN' | 'en' = 'zh-CN'): Promise<ProbeHealthResult> {
  const base = normalizeApiBase(apiBase)
  if (!base) {
    return { ok: false, message: translate(locale, "请填写 API Base URL") }
  }
  try {
    const res = await fetch(`${base}/pub/health`, { signal: AbortSignal.timeout(8_000) })
    if (res.ok) return { ok: true }
    return { ok: false, message: translate(locale, "Host 健康检查失败 (HTTP {0})", [res.status]) }
  } catch {
    return {
      ok: false,
      message: translate(locale, "无法连接 Host，请确认地址正确、服务已启动，且 CORS 已包含当前 Console 来源"),
    }
  }
}

/** 校验 Host API Base + Token，成功则写入 localStorage */
export async function verifyAndStoreCredentials(
  apiBase: string,
  token: string, locale: 'zh-CN' | 'en' = 'zh-CN', persistent = true
): Promise<VerifyCredentialsResult> {
  const base = normalizeApiBase(apiBase)
  const trimmed = token.trim().replace(/^Bearer\s+/i, '')
  if (!base) {
    return { ok: false, message: translate(locale, "请填写 API Base URL（如 http://localhost:8086）") }
  }
  if (!trimmed) {
    return { ok: false, message: translate(locale, "请输入 Token") }
  }

  try {
    const health = await probeHealth(base, locale)
    if (!health.ok) {
      return { ok: false, message: health.message }
    }

    const res = await fetch(`${base}/api/system/status`, {
      headers: { Authorization: `Bearer ${trimmed}` },
      signal: AbortSignal.timeout(8_000),
    })
    if (res.ok) {
      clearRuntimeToken()
      setApiBase(base)
      setToken(trimmed, persistent)
      stripApiBaseUrlFromQuery()
      return { ok: true }
    }
    if (res.status === 401) {
      return { ok: false, status: 401, message: translate(locale, "Token 无效，请检查后重试") }
    }
    return { ok: false, status: res.status, message: translate(locale, "验证失败 (HTTP {0})", [res.status]) }
  } catch {
    return { ok: false, message: translate(locale, "无法连接到 API，请检查 Base URL 与网络") }
  }
}

export function resolveApiUrl(path: string): string {
  const base = getApiBase()
  const p = path.startsWith('/') ? path : `/${path}`
  return `${base}${p}`
}

/** WebSocket 应连用户 Host（如 ws://127.0.0.1:8086/server），而非 Pages 静态域 */
export function resolveWebSocketUrl(path = '/server'): string {
  const httpUrl = resolveApiUrl(path)
  const url = new URL(httpUrl)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.href
}

/** One Host/credential snapshot for a group of requests owned by the same view. */
export interface ApiRequestContext {
  readonly apiBase: string
  readonly token: string | null
}

/** Kept inside a request owner's closure, never persisted or exposed as UI state. */
export function captureApiRequestContext(): ApiRequestContext {
  return Object.freeze({ apiBase: getApiBase(), token: getToken() })
}

/** Fetch with Bearer auth; an explicit context pins relative paths and credentials to their owner. */
export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  context?: ApiRequestContext,
): Promise<Response> {
  const { apiBase, token } = context ?? captureApiRequestContext()
  const headers = new Headers(init?.headers)
  const url =
    typeof input === 'string' && input.startsWith('/')
      ? `${apiBase}${input}`
      : input
  const belongsToHost = requestBelongsToHost(url, apiBase)
  if (token && belongsToHost) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(url, { ...init, headers })
  // An old connection's response must not invalidate a newer Host or credential.
  if (res.status === 401 && belongsToHost && token && getApiBase() === apiBase && getToken() === token) {
    notifyAuthRequired()
  }
  return res
}

function requestBelongsToHost(input: RequestInfo | URL, apiBase: string): boolean {
  try {
    const base = new URL(`${apiBase}/`)
    const target = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url, base)
    return target.origin === base.origin && target.pathname.startsWith(base.pathname)
  } catch { return false }
}
